// A.R.G.O.S config — categories + global tab-limit rules. These rows are
// CONSUMED BY THE DESKTOP CHROME EXTENSION that does the actual browser
// tracking/enforcement (which cannot run on a phone). So this screen edits
// the config; a banner makes the desktop dependency explicit.
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Pressable, StyleSheet, Text, View } from 'react-native';
import { ThemedSwitch } from './ThemedSwitch';
import { makeThemedStyles } from '../../v10/theme';
import { SectionTitle, Field, GoldButton, GlassCard } from '../../v10/ui';
import { supabase } from '../../lib/supabase';

interface EyesCategory {
  id: string;
  name: string;
  emoji: string | null;
  slug: string;
  daily_limit_seconds: number | null;
  tab_limit: number | null;
  is_active: boolean;
  sort_order: number;
}

export default function ArgosEditor({ userId }: { userId: string | undefined }) {
  const styles = useStyles();
  const [categories, setCategories] = useState<EyesCategory[]>([]);
  const [newName, setNewName] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data } = await supabase
      .from('eyes_categories')
      .select('id, name, emoji, slug, daily_limit_seconds, tab_limit, is_active, sort_order')
      .eq('user_id', userId)
      .order('sort_order');
    setCategories((data as EyesCategory[]) ?? []);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const addCategory = async () => {
    const name = newName.trim();
    if (!name || !userId) return;
    const slug = name.toLowerCase().replace(/\s+/g, '-');
    const { data } = await supabase
      .from('eyes_categories')
      .insert({ user_id: userId, name, slug, emoji: '📁', is_active: true, sort_order: categories.length })
      .select('id, name, emoji, slug, daily_limit_seconds, tab_limit, is_active, sort_order')
      .single();
    if (data) setCategories((c) => [...c, data as EyesCategory]);
    setNewName('');
  };

  const updateCategory = async (id: string, patch: Partial<EyesCategory>) => {
    setCategories((c) => c.map((cat) => (cat.id === id ? { ...cat, ...patch } : cat)));
    await supabase.from('eyes_categories').update(patch).eq('id', id);
  };

  const deleteCategory = (id: string) => {
    Alert.alert('Delete category?', undefined, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: async () => {
          setCategories((c) => c.filter((cat) => cat.id !== id));
          await supabase.from('eyes_categories').delete().eq('id', id);
        },
      },
    ]);
  };

  return (
    <View style={styles.content}>
      <GlassCard style={styles.banner}>
        <Text style={styles.bannerText}>
          A.R.G.O.S enforcement runs in the desktop Chrome extension — it can&apos;t track or block
          browsing on a phone. You can still edit the categories it uses here.
        </Text>
      </GlassCard>

      <SectionTitle style={styles.sectionGap}>Categories</SectionTitle>
      {loading ? (
        <Text style={styles.hint}>Loading…</Text>
      ) : (
        categories.map((cat) => (
          <GlassCard key={cat.id} style={styles.card}>
            <View style={styles.rowBetween}>
              <Field
                inputStyle={styles.nameInput}
                value={cat.name}
                onChangeText={(t) => setCategories((c) => c.map((x) => (x.id === cat.id ? { ...x, name: t } : x)))}
                onEndEditing={() => updateCategory(cat.id, { name: cat.name })}
              />
              <ThemedSwitch value={cat.is_active} onValueChange={(v) => updateCategory(cat.id, { is_active: v })} />
            </View>
            <View style={styles.rowBetween}>
              <Text style={styles.fieldLabel}>Daily limit (min)</Text>
              <Field
                inputStyle={styles.numInput}
                keyboardType="number-pad"
                value={cat.daily_limit_seconds ? String(Math.round(cat.daily_limit_seconds / 60)) : ''}
                onChangeText={(t) => {
                  const mins = parseInt(t, 10);
                  updateCategory(cat.id, { daily_limit_seconds: Number.isFinite(mins) ? mins * 60 : null });
                }}
                placeholder="—"
              />
            </View>
            <View style={styles.rowBetween}>
              <Text style={styles.fieldLabel}>Tab limit</Text>
              <Field
                inputStyle={styles.numInput}
                keyboardType="number-pad"
                value={cat.tab_limit != null ? String(cat.tab_limit) : ''}
                onChangeText={(t) => {
                  const n = parseInt(t, 10);
                  updateCategory(cat.id, { tab_limit: Number.isFinite(n) ? n : null });
                }}
                placeholder="—"
              />
            </View>
            <Pressable onPress={() => deleteCategory(cat.id)}>
              <Text style={styles.deleteText}>Delete</Text>
            </Pressable>
          </GlassCard>
        ))
      )}

      <View style={styles.addRow}>
        <Field inputStyle={styles.addInput} value={newName} onChangeText={setNewName} placeholder="New category name" />
        <GoldButton label="Add" onPress={() => void addCategory()} style={styles.actionGap} />
      </View>
    </View>
  );
}

const useStyles = makeThemedStyles(({ t, radii }) =>
  StyleSheet.create({
  actionGap: { marginTop: 8 },
  sectionGap: { marginTop: 18 },
  content: { gap: 8 },
  banner: {
    padding: 12,
    marginTop: 8,
  },
  bannerText: { fontSize: 12, color: t.behind, lineHeight: 17 },
  hint: { fontSize: 12, color: t.ivorySoft, marginTop: 6, fontStyle: 'italic' },
  card: {
    padding: 12,
    marginTop: 8,
    gap: 8,
  },
  rowBetween: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10 },
  nameInput: {
    flex: 1,
    fontWeight: '700',
  },
  fieldLabel: { fontSize: 13, color: t.ivorySoft },
  numInput: {
    minWidth: 70,
    textAlign: 'right',
  },
  deleteText: { fontSize: 13, color: t.lapsed, fontWeight: '700', marginTop: 2 },
  addRow: { flexDirection: 'row', gap: 8, marginTop: 12 },
  addInput: {
    flex: 1,
  },
}),
);
